import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import FullScreenPanel from './UI/FullScreenPanel';
import OpenFullPageLink from './OpenFullPageLink';
import LoanLink from './LoanLink';
import ClientLink from './ClientLink';
import { toCurrency } from '../utils/adminMetrics';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';
import EntityAuditTrail from './EntityAuditTrail';
import DisplayField, { FieldGroup } from './UI/DisplayField';
import { AuthContext } from '../context/AuthContext';

// Quick view of one repayment, read from GET /api/repayments/:id: the payment,
// which payment it was on the loan, where the loan stood after it, and the
// loan and client it belongs to. Everything shown is what the API returns.
// initial: the row already on screen, shown while the full record loads.
const RepaymentDetailsModal = ({ repaymentId, initial = null, onClose, onOpenFullPage = null, showClient = false }) => {
  // The repayment's audit trail (entered, undone, put back): staff only.
  const { user: authUser } = useContext(AuthContext) || {};
  const authPerms = authUser?.permissions || [];
  const canSeeAuditTrail = ['*', 'ViewLoans', 'ManageLoans', 'EnterRepayment', 'AuditTrail', 'Audits'].some((p) => authPerms.includes(p));
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(apiUrl(`/api/repayments/${repaymentId}`), {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'The repayment could not be loaded.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repaymentId]);

  const r = data || {};
  const loan = r.loan || null;
  const loanId = loan?.id ?? r.loan_id;
  const loanCode = loan?.loan_code ?? r.loan_code;
  const hasFull = Boolean(data && data.payment_no !== undefined);

  return (
    <FullScreenPanel
      isOpen
      onClose={onClose}
      title="Repayment Details"
      subtitle={[loanCode, r.payment_date ? formatDateDMY(r.payment_date) : null].filter(Boolean).join(' · ')}
      headerRight={onOpenFullPage && loanId ? <OpenFullPageLink onClick={() => onOpenFullPage(loanId)} /> : null}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 text-sm" role="alert">{error}</div>
      )}
      {loading && !data ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">Loading repayment...</p>
        </div>
      ) : data ? (
        <div className="grid grid-cols-6 gap-4">
          {/* The payment: amount up front, then its particulars. */}
          <section className="col-span-full bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="h-1 bg-green-500" />
            <div className="p-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">Amount paid</div>
                <div className="mt-1 text-3xl font-bold text-green-700 dark:text-green-400 leading-none">{toCurrency(r.amount_paid)}</div>
              </div>
              {hasFull && (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60">
                  Payment {r.payment_no} on the loan
                </span>
              )}
            </div>
            <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <DisplayField label="Payment date">{r.payment_date ? formatDateDMY(r.payment_date) : null}</DisplayField>
              <DisplayField label="Method">{r.payment_method}</DisplayField>
              <DisplayField label="Reference">{r.payment_reference ? <span className="font-mono">{r.payment_reference}</span> : null}</DisplayField>
              <DisplayField label="Entered by">{r.entered_by}</DisplayField>
              <DisplayField label="Entered on">{r.created_at ? formatDateTimeDMY(r.created_at) : null}</DisplayField>
            </div>
          </section>

          {/* Side by side on wide screens, so the whole repayment fits
              without scrolling. */}
          {hasFull && (
            <FieldGroup title="The loan after this payment" tone="emerald" wide bare className="lg:col-span-2">
              <div className="grid grid-cols-2 gap-3">
                <DisplayField label="Paid to date">{toCurrency(r.paid_to_date)}</DisplayField>
                <DisplayField label="Remaining after" tone={Number(r.remaining_after) > 0 ? 'alert' : 'good'}>{toCurrency(r.remaining_after)}</DisplayField>
              </div>
            </FieldGroup>
          )}

          <FieldGroup title={showClient ? 'Loan and client' : 'Loan'} tone="blue" wide bare className={hasFull ? 'lg:col-span-4' : ''}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <DisplayField label="Loan">
              {loanId ? <LoanLink loanId={loanId}>{loanCode || `Loan #${loanId}`}</LoanLink> : null}
            </DisplayField>
            <DisplayField label="Loan status">{loan?.status ?? r.loan_status}</DisplayField>
            {showClient && (
              <DisplayField label="Client">
                {r.client_name ? <ClientLink clientId={r.client_id} name={r.client_name} /> : null}
              </DisplayField>
            )}
            {loan && (
              <>
                <DisplayField label="Principal">{toCurrency(loan.principal_amount)}</DisplayField>
                <DisplayField label="Total repayable">{toCurrency(loan.total_repayable)}</DisplayField>
                <DisplayField label="Monthly payment">{toCurrency(loan.monthly_payment)}</DisplayField>
                <DisplayField label="Duration">
                  {loan.duration_months ? `${loan.duration_months} month${Number(loan.duration_months) === 1 ? '' : 's'}` : null}
                </DisplayField>
                <DisplayField label="Due date">{loan.due_date ? formatDateDMY(loan.due_date) : null}</DisplayField>
              </>
            )}
            </div>
          </FieldGroup>
        </div>
      ) : null}
      {/* Everything that happened to this repayment: always last (staff only). */}
      {repaymentId && canSeeAuditTrail && (
        <EntityAuditTrail
          scope="repayment"
          id={repaymentId}
          title={`Audit trail — repayment #${repaymentId}`}
          className="mt-6"
        />
      )}
    </FullScreenPanel>
  );
};

export default RepaymentDetailsModal;
